/**
 * Public legal documents — Privacy Policy, Terms of Service, Cookie Policy.
 *
 * SCOPE
 * -----
 * Presentational only. Rendered by `App.tsx` for the `#/legal/*` routes for any
 * visitor, authenticated or not — no react-query, no database, no authenticated
 * chrome. Every word describes the application as it is actually built (see
 * docs/architecture.md and docs/user-guide/getting-started.md):
 *
 *   - business records live in the browser's IndexedDB (Dexie), offline-first;
 *   - DNR account authentication handles accounts (email + password + name);
 *   - cloud sync is OPTIONAL, opt-in, and encrypted in transit (HTTPS/TLS);
 *   - backup/restore is a manual file export/import — there is no auto-backup;
 *   - local data is NOT encrypted at rest;
 *   - there is no in-app account-deletion yet — requests go to support;
 *   - there are no analytics or advertising cookies.
 *
 * It deliberately makes NO claim of GST or tax compliance, NO claim of
 * encryption at rest, NO claim of automatic backup, and NO claim of permanent
 * deletion — because the implementation supports none of those. It does not
 * exclude non-excludable consumer rights or statutory responsibilities.
 */

export type LegalDocSlug = 'privacy' | 'terms' | 'cookies';

interface LegalSection {
  readonly heading: string;
  readonly body: readonly string[];
}

interface LegalDoc {
  readonly slug: LegalDocSlug;
  readonly title: string;
  readonly intro: string;
  readonly sections: readonly LegalSection[];
}

const LAST_UPDATED = 'Last updated: 13 October 2026';
const CONTACT_EMAIL = 'support@dnrvyapar.in';

const PRIVACY: LegalDoc = {
  slug: 'privacy',
  title: 'Privacy Policy',
  intro:
    'This Privacy Policy explains what information DNR collects, where it is stored, and the choices you have. It is written to match how the application actually works today.',
  sections: [
    {
      heading: '1. Who we are',
      body: [
        `DNR is an offline-first invoicing and document-management application for small businesses. It is operated from India. For any privacy question or request, contact us at ${CONTACT_EMAIL}.`,
      ],
    },
    {
      heading: '2. Information you provide',
      body: [
        'Account information: when you create an account we collect your email address, a password, and your name. Passwords are handled by our authentication provider (DNR account service) and are never stored in your business data.',
        'Business and customer records: the invoices, quotations, sales orders, delivery notes, payments, products, and party (customer/supplier) details you enter. These are your records, and you control their accuracy.',
      ],
    },
    {
      heading: '3. Where your data is stored',
      body: [
        'Local storage: your business records are stored on your device in your browser\'s IndexedDB database (via Dexie). They remain on your device and are not uploaded unless you enable the optional features below. A short session flag is also kept in your browser\'s sessionStorage while an email confirmation is being processed.',
        'Cloud synchronisation (optional): if you choose to enable sync, your records are transmitted to and stored with our cloud provider (DNR sync service) so they can be restored on another device. Sync is off by default, is entirely opt-in, and data is encrypted in transit using HTTPS/TLS. Access to synced data is restricted by row-level security tied to your account.',
        'Authentication and session: your account credentials and active session are managed by the DNR account service.',
      ],
    },
    {
      heading: '4. Security',
      body: [
        'Data sent between your browser and our servers is encrypted in transit using HTTPS/TLS.',
        'Important limitation: business records stored locally in your browser are not encrypted at rest. Anyone with access to your device and browser profile may be able to read them. Please protect your device with a lock screen and keep your backups in a safe place.',
      ],
    },
    {
      heading: '5. Backups, retention, and deletion',
      body: [
        'There is no automatic backup. You can export your data yourself as a JSON or CSV file from Settings → Backup & Restore at any time. Keeping an external backup is your responsibility.',
        'Your local data remains on your device until you delete it or clear your browser data. Clearing your browser data or using the app on a new device without a backup may permanently remove local records.',
        'If you have enabled sync, deleting the app or clearing browser data does not delete the synced copy in the cloud. To request deletion of cloud copies or your account, email us at ' + CONTACT_EMAIL + '.',
        'At present, account deletion is handled on request rather than through an in-app button. When you request deletion, we will act on the request and confirm once complete.',
      ],
    },
    {
      heading: '6. Your rights',
      body: [
        'You can access and correct your information at any time within the app, and export it using the backup feature.',
        'To request access, correction, or deletion, or to withdraw your consent to optional cloud sync, contact us at ' + CONTACT_EMAIL + '. You can disable sync at any time in Settings.',
      ],
    },
    {
      heading: '7. Third-party services',
      body: [
        'We use the DNR account service for authentication and optional cloud storage/synchronisation, and Cloudflare Pages for hosting the application. We do not use advertising networks, and we do not sell your data.',
      ],
    },
    {
      heading: '8. Cookies and similar technologies',
      body: [
        'We do not use advertising or analytics cookies. Our authentication provider stores a session token in your browser to keep you signed in. See the Cookie Policy for details.',
      ],
    },
    {
      heading: '9. Data protection law',
      body: [
        'We aim to handle personal data in line with the Digital Personal Data Protection Act, 2023 and the Digital Personal Data Protection Rules, 2025. The substantive duties for data fiduciaries (notice, security safeguards, breach reporting, retention, and data-principal rights) commence on 13 May 2027 under those Rules. We track these obligations and will update this policy as those duties take effect.',
      ],
    },
    {
      heading: '10. Changes to this policy',
      body: [
        'If we make material changes, we will update the "Last updated" date above and, where appropriate, notify you in the app or by email.',
      ],
    },
  ],
};


const TERMS: LegalDoc = {
  slug: 'terms',
  title: 'Terms of Service',
  intro:
    'These Terms govern your use of DNR. By creating an account or using the app, you agree to them.',
  sections: [
    {
      heading: '1. The service',
      body: [
        'DNR provides tools for preparing and managing everyday business documents — such as quotations, sales orders, delivery notes, invoices, and payment records — and works offline-first, with optional cloud synchronisation.',
        'Some features may still be in development, and availability can change. We will communicate any future pricing changes before they take effect.',
      ],
    },
    {
      heading: '2. Your responsibilities',
      body: [
        'DNR provides tools for preparing and managing business documents. You are responsible for verifying document accuracy, applicable tax treatment, statutory compliance, and maintaining appropriate backups of your business records.',
        'You agree to use the app lawfully and not to upload content that infringes others\' rights or violates any law.',
      ],
    },
    {
      heading: '3. GST settings and tax calculations',
      body: [
        'The app can help you record tax rates and calculate amounts based on the settings and data you provide. It is a calculation and record-keeping aid only.',
        'DNR does not file your returns and does not guarantee that any document is compliant with GST or any other law. Tax treatment depends on your specific facts, registration status, place of supply, and applicable law. Please confirm the correct treatment for each transaction and consult a qualified professional where needed.',
      ],
    },
    {
      heading: '4. Accounts and security',
      body: [
        'You are responsible for keeping your login credentials secure and for all activity under your account. Notify us promptly at ' + CONTACT_EMAIL + ' if you believe your account has been compromised.',
      ],
    },
    {
      heading: '5. Your data',
      body: [
        'You own your business data. We do not claim ownership over it. You can export it at any time from Settings → Backup & Restore.',
      ],
    },
    {
      heading: '6. Backups, offline use, and sync limitations',
      body: [
        'There is no automatic backup; keeping your own backups is your responsibility.',
        'The app is designed to work offline, but cloud synchronisation (if you enable it) depends on your internet connection and our cloud provider, and may occasionally fail or experience conflicts. We are not responsible for data loss caused by clearing browser data, device loss, or failure to back up.',
      ],
    },
    {
      heading: '7. Service availability and changes',
      body: [
        'We may change, suspend, or discontinue features, and may interrupt the service for maintenance. We are not liable for any loss arising from such changes or interruptions.',
      ],
    },
    {
      heading: '8. Free access and future pricing',
      body: [
        'You can use the current version at no cost. Some features may still be in development. If we introduce paid features in future, we will give notice before any charge takes effect.',
      ],
    },
    {
      heading: '9. Closing your account',
      body: [
        'You may stop using the app at any time. To request deletion of your account and cloud data, email ' + CONTACT_EMAIL + '. Local data on your device is under your control and can be deleted by clearing the app data in your browser.',
      ],
    },
    {
      heading: '10. Intellectual property',
      body: [
        'The DNR name, logo, and software remain our property. These Terms do not give you any right to copy or resell the software beyond using it for your own business.',
      ],
    },
    {
      heading: '11. Complaints and disputes',
      body: [
        'Please contact us first at ' + CONTACT_EMAIL + ' so we can resolve any concern. These Terms are governed by the laws of India, and the courts in India will have jurisdiction. Nothing in these Terms limits any rights you have under applicable law that cannot be excluded.',
      ],
    },
    {
      heading: '12. Limitation of liability',
      body: [
        'To the extent permitted by law, DNR is provided "as is", and we are not liable for indirect or consequential loss, loss of profit, or loss of data arising from your use of the app. Nothing here excludes liability for fraud, or for any other liability that cannot lawfully be excluded.',
      ],
    },
  ],
};

const COOKIES: LegalDoc = {
  slug: 'cookies',
  title: 'Cookie Policy',
  intro:
    'This policy explains the browser technologies DNR uses. We keep this list accurate and minimal on purpose.',
  sections: [
    {
      heading: '1. What we use',
      body: [
        'Authentication session: to keep you signed in, our authentication provider stores a session token in your browser\'s local storage. This is essential for the app to function and is not used for advertising.',
        'Application data: your business records are stored in your browser\'s IndexedDB, and a short signup-confirmation flag may be kept in sessionStorage while an email confirmation is processed. These are local storage, not cookies.',
        'Offline support: the app uses a service worker to cache assets so it can load offline. This is a caching mechanism, not a tracking technology.',
      ],
    },
    {
      heading: '2. What we do not use',
      body: [
        'We do not use advertising cookies, third-party analytics, or cross-site tracking, and we do not run third-party tracking scripts. Because we set no non-essential cookies, no cookie-consent banner is required for the current app.',
        'If we ever introduce non-essential cookies or analytics in the future, we will update this policy and request your consent where required by law.',
      ],
    },
    {
      heading: '3. Managing storage',
      body: [
        'You can clear cookies, site data, and local storage at any time through your browser settings. Note that clearing site data will also remove your locally stored business records unless you have exported a backup, and you may need to sign in again.',
      ],
    },
    {
      heading: '4. Contact',
      body: [`Questions about this policy? Email us at ${CONTACT_EMAIL}.`],
    },
  ],
};

const LEGAL_DOCS: Record<LegalDocSlug, LegalDoc> = {
  privacy: PRIVACY,
  terms: TERMS,
  cookies: COOKIES,
};

/** Maps a public hash route to its document, or null when it is not a legal route. */
export function legalDocForHash(hashBase: string): LegalDocSlug | null {
  switch (hashBase) {
    case '#/legal/privacy':
      return 'privacy';
    case '#/legal/terms':
      return 'terms';
    case '#/legal/cookies':
      return 'cookies';
    default:
      return null;
  }
}

export function LegalPage({ doc }: { readonly doc: LegalDocSlug }) {
  const legal = LEGAL_DOCS[doc];
  return (
    <div className="min-h-screen bg-ivory text-ink">
      <header className="sticky top-0 z-50 border-b border-slate-300/20 bg-ink/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-4 sm:px-6">
          <a href="#/" className="flex items-center gap-2.5" aria-label="DNR home">
            <img src="/assets/logo.svg" alt="DNR logo" className="h-9 w-9 rounded-xl" />
            <span className="text-base font-extrabold tracking-tight text-fog">DNR</span>
            <span className="text-xs font-medium text-gold uppercase tracking-wider hidden sm:inline-block">DIGITAL NET REGISTER</span>
          </a>
          <a href="#/" className="text-xs font-medium text-mist transition-colors hover:text-fog hover:underline">
            ← Back to home
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
        <h1 className="text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">{legal.title}</h1>
        <p className="mt-2 text-xs font-medium text-mist">{LAST_UPDATED}</p>
        <p className="mt-6 text-base leading-7 text-slate-ink">{legal.intro}</p>

        <div className="mt-10 space-y-8">
          {legal.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="text-lg font-bold text-ink">{section.heading}</h2>
              <div className="mt-3 space-y-3">
                {section.body.map((paragraph, i) => (
                  <p key={i} className="text-sm leading-6 text-slate-ink/90">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-12 border-t border-slate-300/20 pt-6 text-sm">
          <p className="text-mist">
            Questions? Email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-blue hover:underline">
              {CONTACT_EMAIL}
            </a>
            .
          </p>
          <div className="mt-4 flex flex-wrap gap-5 text-xs text-mist">
            <a href="#/legal/privacy" className="hover:text-ink hover:underline">Privacy</a>
            <a href="#/legal/terms" className="hover:text-ink hover:underline">Terms</a>
            <a href="#/legal/cookies" className="hover:text-ink hover:underline">Cookie policy</a>
          </div>
        </div>
      </main>
    </div>
  );
}

